import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'

import { AccountStatus, AccountUserType } from '__generated__/atlas-types'

import {
  getAccountUserType,
  isProBrand,
  userAccountStatus,
  userIsAdmin,
  userIsLoggedInAsAdmin,
} from 'store/currentUser/selectors'

interface IUserPermissionsSelectors {
  isAdmin: boolean
  isLoggedInAsAdmin: boolean
  userType: AccountUserType
  isProBrandAccount: boolean
}

/**
 * This hook is used to check if the viewing user has access to the user management page.
 * @returns {boolean} - Whether the viewing user has access to the user management page.
 */
export const useViewingUserManagementAccess = () => {
  const {
    isAdmin,
    isLoggedInAsAdmin,
    userType,
    isProBrandAccount,
    accountStatus,
  } = useSelector<
    object,
    IUserPermissionsSelectors & { accountStatus: AccountStatus }
  >((state) => ({
    isAdmin: userIsAdmin(state),
    isLoggedInAsAdmin: userIsLoggedInAsAdmin(state),
    userType: getAccountUserType(state),
    isProBrandAccount: isProBrand(state),
    accountStatus: userAccountStatus(state),
  }))
  const { selfServeUserManagement } = useFlags()

  // For historical accounts, only internal users can access the user management page.
  if (
    !isProBrandAccount ||
    (accountStatus === AccountStatus.HISTORICAL &&
      userType !== AccountUserType.INTERNAL)
  ) {
    return false
  }

  const isAdminOrInternal =
    userType === AccountUserType.ADMIN ||
    userType === AccountUserType.INTERNAL ||
    isAdmin ||
    isLoggedInAsAdmin

  if (isAdminOrInternal) {
    return selfServeUserManagement
  }

  return false
}

/**
 * This hook is used to check if the viewing user has access to the permission settings page.
 * and if they can manage permissions.
 * @returns {Object} - An object with the following properties:
 * - canViewPermissions: boolean - Whether the viewing user has access to the permission settings page.
 * - canManagePermissions: boolean - Whether the viewing user can manage permissions.
 */
export const useViewingUserPermissionSettingsAccess = () => {
  const { isAdmin, isLoggedInAsAdmin, userType, isProBrandAccount } =
    useSelector<object, IUserPermissionsSelectors>((state) => ({
      isAdmin: userIsAdmin(state),
      isLoggedInAsAdmin: userIsLoggedInAsAdmin(state),
      userType: getAccountUserType(state),
      isProBrandAccount: isProBrand(state),
    }))
  const { newPermissionsPage } = useFlags()

  if (!isProBrandAccount) {
    return {
      canViewPermissions: false,
      canManagePermissions: false,
      canSeeNavbarOption: false,
      newPermissionsPage,
    }
  }

  if (userType === AccountUserType.NO_ACCESS) {
    return {
      canViewPermissions: false,
      canManagePermissions: false,
      canSeeNavbarOption: false,
      newPermissionsPage,
    }
  }

  const isAdminOrInternal =
    userType === AccountUserType.ADMIN ||
    userType === AccountUserType.INTERNAL ||
    isAdmin ||
    isLoggedInAsAdmin

  if (isAdminOrInternal) {
    return {
      canViewPermissions: true,
      canManagePermissions: true,
      canSeeNavbarOption: false,
      newPermissionsPage,
    }
  }

  return {
    canViewPermissions: true,
    canManagePermissions: false,
    canSeeNavbarOption: true,
    newPermissionsPage,
  }
}
